/**
 * Customer_Aux admin page — view + edit per-customer discount tiers and
 * payment terms. Reads from the live MySQL Customer_Aux table.
 *
 * Route: /admin/customer-aux
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import AccountMenu from "@/components/AccountMenu";
import DealerAgreementSignModal from "@/components/DealerAgreementSignModal";

export default function CustomerAuxPage({ user, onLogout, onLogoutAll }) {
  const t = useT();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [signing, setSigning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signedStatuses, setSignedStatuses] = useState({});

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/customer-aux", { params: { q: search, limit: 200 } });
      const list = r.data?.items || [];
      setItems(list);
      // In parallel, probe sign-status for dealer-type rows so the badge/button
      // reflects reality immediately.
      const dealers = list.filter(it => isDealerLikeType(it.customer_type));
      const statuses = {};
      await Promise.all(dealers.map(async it => {
        try {
          const s = await api.get(`/admin/dealers/${encodeURIComponent(it.customer_id)}/agreement-status`);
          statuses[it.customer_id] = s.data?.signed === true;
        } catch { statuses[it.customer_id] = false; }
      }));
      setSignedStatuses(statuses);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Customer_Aux");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const id = setTimeout(refresh, search ? 350 : 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const save = async () => {
    if (!editing?.customer_id) return;
    try {
      const payload = {
        customer_id: editing.customer_id,
        discount_d_series: Number(editing.discount_d_series) || 0,
        discount_b_series: Number(editing.discount_b_series) || 0,
        discount_spare_part: Number(editing.discount_spare_part) || 0,
        payment_term_days: Number(editing.payment_term_days) || 0,
        open_account_limit: Number(editing.open_account_limit) || 0,
        note: editing._approval_note || "",
      };
      const r = await api.post("/admin/discount-approvals", payload);
      if (r.data?.status === "approved") {
        toast.success(t("aux.saved"));
      } else {
        toast.success(t("aux.approval_requested"), {
          description: t("aux.approval_requested_desc"),
        });
      }
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 px-4 md:px-8 h-14 flex items-center gap-3 max-w-7xl mx-auto">
        <button data-testid="aux-back-home" onClick={() => navigate("/")}
          className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"
          title={t("aux.back_home_tooltip")}>
          <ArrowLeft size={18} weight="bold" />
        </button>
        <Tag size={20} weight="fill" className="text-violet-300" />
        <h1 className="text-base font-black tracking-tighter font-['Chivo']" data-testid="aux-title">
          {t("aux.page_title")}
        </h1>
        <div className="ml-auto"><AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} /></div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <p className="text-sm text-slate-600 max-w-3xl">{t("aux.subtitle")}</p>
        <input data-testid="aux-search"
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder={t("aux.search_placeholder")}
          className="w-full md:w-96 border rounded px-3 py-2 text-sm" />

        <div className="bg-white border rounded-md overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="aux-table">
            <thead className="bg-slate-50 border-b text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">{t("aux.col_customer_id")}</th>
                <th className="text-left px-3 py-2">{t("aux.col_company")}</th>
                <th className="text-left px-3 py-2">{t("aux.col_type")}</th>
                <th className="text-right px-3 py-2">{t("aux.col_d_series")}</th>
                <th className="text-right px-3 py-2">{t("aux.col_b_series")}</th>
                <th className="text-right px-3 py-2">{t("aux.col_spare")}</th>
                <th className="text-right px-3 py-2">{t("aux.col_net")}</th>
                <th className="text-right px-3 py-2">{t("aux.col_open_limit")}</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && <tr><td colSpan={9} className="p-6 text-center text-sm text-slate-500">{t("aux.loading")}</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-sm text-slate-500">{t("aux.empty")}</td></tr>}
              {!loading && items.map(it => (
                <tr key={it.customer_id} data-testid={`aux-row-${it.customer_id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{it.customer_id}</td>
                  <td className="px-3 py-2">{it.company_name || "—"}</td>
                  <td className="px-3 py-2 text-xs">{it.customer_type || "—"}</td>
                  <td className="px-3 py-2 text-right">{pctOff(it.discount_d_series)}</td>
                  <td className="px-3 py-2 text-right">{pctOff(it.discount_b_series)}</td>
                  <td className="px-3 py-2 text-right">{pctOff(it.discount_spare_part)}</td>
                  <td className="px-3 py-2 text-right">Net-{it.payment_term_days || 0}</td>
                  <td className="px-3 py-2 text-right">${(it.open_account_limit || 0).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <button data-testid={`aux-edit-${it.customer_id}`}
                      onClick={() => setEditing({ ...it })}
                      className="text-xs text-violet-700 hover:text-violet-900 font-bold mr-2">
                      {t("aux.edit")}
                    </button>
                    {isDealerLikeType(it.customer_type) && (
                      <>
                        {signedStatuses[it.customer_id] ? (
                          <span
                            data-testid={`aux-agreement-signed-${it.customer_id}`}
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300 mr-1">
                            ✓ {t("aux.dealer_agreement_signed")}
                          </span>
                        ) : null}
                        <button
                          data-testid={`aux-sign-${it.customer_id}`}
                          onClick={() => setSigning(it)}
                          className="text-xs text-blue-700 hover:text-blue-900 font-bold mr-2">
                          {signedStatuses[it.customer_id]
                            ? t("aux.dealer_agreement_resign")
                            : t("aux.dealer_agreement_sign")}
                        </button>
                        <a data-testid={`aux-agreement-${it.customer_id}`}
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/dealers/${encodeURIComponent(it.customer_id)}/agreement.pdf`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-xs text-slate-600 hover:text-slate-900 font-bold">
                          {t("aux.dealer_agreement_pdf")}
                        </a>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-md shadow-xl max-w-lg w-full p-5" data-testid="aux-edit-modal">
            <h3 className="text-lg font-bold mb-3">
              {t("aux.edit_title", null, { name: editing.company_name || editing.customer_id })}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <Pct label={t("aux.col_d_series")} value={editing.discount_d_series}
                onChange={v => setEditing({ ...editing, discount_d_series: v })} testid="aux-edit-d-series" />
              <Pct label={t("aux.col_b_series")} value={editing.discount_b_series}
                onChange={v => setEditing({ ...editing, discount_b_series: v })} testid="aux-edit-b-series" />
              <Pct label={t("aux.col_spare")} value={editing.discount_spare_part}
                onChange={v => setEditing({ ...editing, discount_spare_part: v })} testid="aux-edit-spare" />
              <Num label={t("aux.col_net")} value={editing.payment_term_days}
                onChange={v => setEditing({ ...editing, payment_term_days: v })} testid="aux-edit-net" />
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider font-bold text-slate-600 block mb-1">
                  {t("aux.col_open_limit")}
                </label>
                <input data-testid="aux-edit-limit" type="number" min={0}
                  value={editing.open_account_limit || 0}
                  onChange={e => setEditing({ ...editing, open_account_limit: e.target.value })}
                  className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider font-bold text-slate-600 block mb-1">
                  {t("aux.approval_note_label")}
                </label>
                <textarea data-testid="aux-edit-note"
                  rows={2}
                  placeholder={t("aux.approval_note_placeholder")}
                  value={editing._approval_note || ""}
                  onChange={e => setEditing({ ...editing, _approval_note: e.target.value })}
                  className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-3 italic">{t("aux.approval_workflow_hint")}</p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-3 py-2 text-sm border rounded">
                {t("aux.cancel")}
              </button>
              <button data-testid="aux-save"
                onClick={save}
                className="px-4 py-2 bg-violet-600 text-white rounded font-bold text-sm hover:bg-violet-700">
                {t("aux.propose_save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {signing && (
        <DealerAgreementSignModal
          customer={signing}
          onClose={() => setSigning(null)}
          onSigned={() => { setSigning(null); refresh(); }}
        />
      )}
    </div>
  );
}

function Pct({ label, value, onChange, testid }) {
  // The MySQL column is the NET-PRICE MULTIPLIER (0.00-1.00).
  // 0.85 = customer pays 85% (15% OFF). 0.00 = no aux discount on file.
  // We display & edit "% OFF" for clarity but store the multiplier.
  const off = value > 0 ? Math.round((1 - value) * 100) : 0;
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-bold text-slate-600 block mb-1">
        {label} <span className="text-slate-400 font-normal">(% OFF)</span>
      </label>
      <div className="flex items-center gap-1">
        <input data-testid={testid}
          type="number" min={0} max={100} step={1}
          value={off}
          onChange={e => {
            // Convert "% OFF" back to the stored multiplier.
            // 0% off → 0.00 (no discount on file). 15% off → 0.85.
            const off_pct = Math.max(0, Math.min(100, Number(e.target.value) || 0));
            onChange(off_pct === 0 ? 0 : (1 - off_pct / 100));
          }}
          className="w-full border rounded px-2 py-1.5 text-sm text-right" />
        <span className="text-sm text-slate-500">%</span>
      </div>
    </div>
  );
}

// Renders the stored multiplier (0.00-1.00) as "X% OFF" in tables.
function pctOff(multiplier) {
  if (!multiplier || multiplier <= 0) return "—";
  return `${Math.round((1 - multiplier) * 100)}% OFF`;
}

// Detects dealer / sub-dealer / distributor customer types so we surface
// the dealer-warranty-agreement download button only on those rows.
function isDealerLikeType(type) {
  const t = (type || "").trim().toLowerCase();
  return t === "distributor" || t === "reseller" || t === "sub dealer"
    || t === "sub-dealer" || t === "oem";
}

function Num({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-bold text-slate-600 block mb-1">{label}</label>
      <input data-testid={testid} type="number" min={0} max={365}
        value={value || 0}
        onChange={e => onChange(Number(e.target.value) || 0)}
        className="w-full border rounded px-2 py-1.5 text-sm text-right" />
    </div>
  );
}
